import { shortUuid } from '@tunasong/models'
import type { AudioPeaks } from '@tunasong/models'
import { isSafari, useContainerSize, useMergeRefs } from '@tunasong/ui-lib'
import cn from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import { generateBars } from '../lib/wave-drawer.js'

import type { ComponentType, HTMLAttributes, SVGProps } from 'react'
import styles from './wave.module.css'

const Badge = (props: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cn(styles.badge, props.className)} style={props.style} />
)

const WaveformContainer = (props: SVGProps<SVGSVGElement>) => (
  <svg {...props} className={cn(styles.waveformContainer, props.className)} style={props.style} />
)

interface ClipPathRectProps extends SVGProps<SVGRectElement> {
  clipPathUrl?: string
}

const WaveformBackground = ({ clipPathUrl, ...props }: ClipPathRectProps) => (
  <rect {...props} className={cn(styles.waveformBackground, props.className)} style={{ clipPath: clipPathUrl, ...props.style }} />
)

const WaveformProgress = ({ clipPathUrl, ...props }: ClipPathRectProps) => (
  <rect {...props} className={cn(styles.waveformProgress, props.className)} style={{ clipPath: clipPathUrl, ...props.style }} />
)

export interface WaveProps extends Omit<HTMLAttributes<HTMLDivElement>, 'ref'> {
  className?: string
  /** `fixed`: render each peak one time. `dynamic` fill the available box, calculate peak characteristics  */
  mode?: 'fixed' | 'dynamic'
  peaks?: AudioPeaks
  badge?: string | null
  /** Position in the wave, from 0.0 to 1.0 */
  position?: number
  barWidth?: number
  component?: ComponentType<HTMLAttributes<HTMLDivElement>>
  ref?: React.Ref<HTMLDivElement>
}

export const Wave = (props: WaveProps) => {
  const {
    className,
    badge,
    peaks,
    barWidth = 5,
    position = 0,
    mode = 'dynamic',
    component: Component,
    ref: rootRef,
    ...restProps
  } = props
  const clipId = useMemo(() => `waveform-${shortUuid()}`, [])
  const clipPath = `#${clipId}`
  const clipPathUrl = `url("${clipPath}")`
  const progressWidth = position * 100

  const [localRef, setLocalRef] = useState<HTMLDivElement | null>(null)
  const mergedRef = useMergeRefs(setLocalRef, rootRef)
  const maxBuckets = mode === 'fixed' ? (peaks?.length ?? 0) : Math.min(peaks?.length ?? 200, 200)

  const spacing = 0.2

  const containerSize = useContainerSize(localRef)
  const numBuckets =
    mode === 'fixed'
      ? (peaks?.length ?? 0)
      : containerSize?.width
        ? Math.min(maxBuckets, containerSize.width / (barWidth + spacing))
        : 0

  const bars = peaks ? generateBars({ peaks, numBuckets, spacing }) : []

  /** Hack to enable wave is rendering on Safari */
  const [renderKey, setRenderKey] = useState(shortUuid())
  useEffect(() => {
    if (!isSafari()) {
      return
    }
    setTimeout(() => {
      setRenderKey(shortUuid())
    }, 1000)
  }, [])

  const Container = Component ?? 'div'

  return (
    <Container
      {...restProps}
      className={cn(styles.wave, className)}
      ref={mergedRef as React.Ref<HTMLDivElement>}
      style={restProps.style}
    >
      {badge ? <Badge>{badge}</Badge> : null}

      <WaveformContainer viewBox="0 0 100 100" preserveAspectRatio="none">
        <WaveformBackground clipPathUrl={clipPathUrl} x="0" y="0" height="100" width="100" />
        <WaveformProgress clipPathUrl={clipPathUrl} x={0} y={0} height={100} width={progressWidth} />
      </WaveformContainer>
      {/* this SVG is the "clipping mask" - the waveform bars */}
      <svg height="0" width="0">
        <defs>
          <clipPath key={renderKey} id={clipId}>
            {bars}
          </clipPath>
        </defs>
      </svg>
    </Container>
  )
}

export default Wave
